import { graphqlFetch } from '@/utils/graphqlFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExerciseInput,
  ExerciseOutput,
  MutationCreateExerciseArgs,
  MutationRemoveExerciseArgs,
  MutationRemoveQuestionArgs,
  MutationUpdateExerciseArgs,
  QueryExercisesByLessonArgs,
  QuestionOutput,
} from '../gql/graphql';
import { CREATE_EXERCISE } from '../mutations';
import { REMOVE_QUESTION, UPDATE_EXERCISE } from '../mutations/exercise';
import {
  GET_EXERCISES_BY_LESSON,
  GET_EXERCISE_BY_ID,
  REMOVE_EXERCISE,
} from '../queries/exercise';

export const useGetExercisesByLesson = (lessonId: string) => {
  return useQuery({
    queryKey: ['exercises', lessonId],
    queryFn: () =>
      graphqlFetch<
        {
          exercisesByLesson: ExerciseOutput[];
        },
        QueryExercisesByLessonArgs
      >(GET_EXERCISES_BY_LESSON, { lessonId }),
  });
};

export const useCreateExercise = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ExerciseInput) => {
      const data = await graphqlFetch<
        {
          createExercise: ExerciseOutput;
        },
        MutationCreateExerciseArgs
      >(CREATE_EXERCISE, { input });
      return data.createExercise;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['exercises'],
      });
      queryClient.invalidateQueries({
        queryKey: ['exercisesWithDisciplinesAndSeries'],
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });
};

export const useGetExerciseById = (id: string) => {
  return useQuery<ExerciseOutput, Error>({
    queryKey: ['exercise', id],
    queryFn: async () => {
      const data = await graphqlFetch<
        { exercise: ExerciseOutput },
        { id: string }
      >(GET_EXERCISE_BY_ID, { id });
      return data?.exercise;
    },
    enabled: !!id,
  });
};

export const useUpdateExercise = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: MutationUpdateExerciseArgs) => {
      const data = await graphqlFetch<
        { updateExercise: ExerciseOutput },
        MutationUpdateExerciseArgs
      >(UPDATE_EXERCISE, { id, input });
      return data.updateExercise;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['exercises'],
      });
      queryClient.invalidateQueries({
        queryKey: ['exercisesWithDisciplinesAndSeries'],
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });
};

export const useDeleteExercise = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const data = await graphqlFetch<
        { deleteExercise: ExerciseOutput },
        MutationRemoveExerciseArgs
      >(REMOVE_EXERCISE, { id });
      return data.deleteExercise;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['exercises'],
      });
      queryClient.invalidateQueries({
        queryKey: ['exercisesWithDisciplinesAndSeries'],
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });
};

export const useRemoveQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const data = await graphqlFetch<
        { deleteQuestion: QuestionOutput },
        MutationRemoveQuestionArgs
      >(REMOVE_QUESTION, { id });
      return data.deleteQuestion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['exercises'],
      });
      queryClient.invalidateQueries({
        queryKey: ['exercisesWithDisciplinesAndSeries'],
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });
};
